
import { supabase } from "@/integrations/supabase/client";
import { FamilyMember, FamilyInviteResult, FamilyAdminCheckResult } from "@/types/database";
import { handleServiceError } from "../utils/errorHandling";
import { familyAdminService } from "./familyAdminService";

export const familyMembershipService = {
  /**
   * Gets family members for a user's current family
   * Uses the get_family_members database function which relies on current_family_id
   */
  getFamilyMembers: async (userId: string): Promise<FamilyMember[]> => {
    try {
      // Use the database function to get family members
      // This function internally uses current_family_id
      const { data, error } = await supabase
        .rpc('get_family_members', { p_user_id: userId });
      
      if (error) throw error;
      
      // Map the results to the expected format
      const members: FamilyMember[] = data.map(member => ({
        id: member.id,
        first_name: member.first_name,
        last_name: member.last_name,
        family_role: member.family_role
      }));
      
      return members || [];
    } catch (error) {
      handleServiceError(error, "Failed to get family members");
      return [];
    }
  },

  /**
   * Creates a new family by updating user's profile to make their registration_family_id their current_family_id
   * Since each user already has a unique registration_family_id, this just activates their own family
   */
  createNewFamily: async (userId: string, familyName: string): Promise<FamilyInviteResult> => {
    try {
      if (!familyName.trim()) {
        return {
          success: false,
          message: "Family name cannot be empty"
        };
      }

      // Get user's profile to get their registration_family_id
      const { data: userProfile, error: profileError } = await supabase
        .from('profiles')
        .select('registration_family_id')
        .eq('id', userId)
        .single();
      
      if (profileError) throw profileError;
      
      if (!userProfile?.registration_family_id) {
        return {
          success: false,
          message: "User registration family ID not found"
        };
      }

      // Update user to use their registration_family_id as current_family_id and set family name in family_role
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ 
          current_family_id: userProfile.registration_family_id,
          family_role: familyName.trim()
        })
        .eq('id', userId);
      
      if (updateError) throw updateError;

      return {
        success: true,
        message: "Family created successfully",
        familyId: userProfile.registration_family_id
      };
    } catch (error: any) {
      handleServiceError(error, "Failed to create family");
      return { 
        success: false, 
        message: error.message || "An error occurred while creating the family"
      };
    }
  },

  /**
   * Remove a member from a family (resets to their registration family)
   * Uses the remove_from_family database function which updates current_family_id
   */
  removeFamilyMember: async (memberId: string): Promise<{ 
    success: boolean; 
    message: string 
  }> => {
    try {
      // Use the database function to reset a user to their registration family
      // This function updates current_family_id to be the same as registration_family_id
      const { data, error } = await supabase
        .rpc("remove_from_family", { p_user_id: memberId });
      
      if (error) throw error;

      return { 
        success: true, 
        message: "Family member removed successfully." 
      };
    } catch (error) {
      handleServiceError(error, "Failed to remove family member");
      return { 
        success: false, 
        message: "An error occurred while removing the family member." 
      };
    }
  },

  /**
   * Get count of members in a family
   * Used to determine if a family is single-member
   */
  getFamilyMemberCount: async (userId: string): Promise<number> => {
    try {
      const members = await familyMembershipService.getFamilyMembers(userId);
      return members.length;
    } catch (error) {
      handleServiceError(error, "Failed to check if family has single member");
      return 0;
    }
  },

  /**
   * Check if a user can leave their family as an admin
   * This uses isHouseholdAdmin which checks if registration_family_id = current_family_id
   */
  canUserLeaveFamilyAsAdmin: async (userId: string): Promise<FamilyAdminCheckResult> => {
    try {
      // First check if user is an admin
      const isAdmin = await familyAdminService.isHouseholdAdmin(userId);
      
      if (!isAdmin) {
        // If user is not an admin, they can leave
        return { isAdmin: false, canLeave: true };
      }
      
      // Count family members
      const members = await familyMembershipService.getFamilyMembers(userId);
      const memberCount = members.length;
      
      // Admin can leave only if they're the only member
      const canLeave = memberCount <= 1;
      
      return { 
        isAdmin: true, 
        canLeave: canLeave,
        message: canLeave ? 
          undefined : 
          "You are the family admin. You need to remove all other members before you can leave or switch families."
      };
    } catch (error) {
      handleServiceError(error, "Failed to check if user can leave family");
      return { isAdmin: false, canLeave: false, message: "Failed to check if you can leave your family. Please try again." };
    }
  }
};
